import { RefObject } from 'react';
import {
  ActorRef, BaseActionObject, DoneInvokeEvent, Event, EventData, ResolveTypegenMeta, SCXML, ServiceMap, SingleOrArray, State, TypegenDisabled,
} from 'xstate';
import { IWidgetConfig } from './configInterface';
import { CAMERA_STATES_MESSAGES, INTERNET_STATES_MESSAGES, MICROPHONE_STATES_MESSAGES } from '../utils/messages.utils';
import { TAKE_TYPES } from './storageInterface';
import { ALL_JOB_QUESTIONS_TYPE } from './jobInterface';
import { IVideoError } from '../utils/errors.utils';
import { IVideoDimensions } from './videoInterface';

type IUploaderRef = {
  [uploaderRef: string]: ActorRef<DoneInvokeEvent<any>>;
};

export const VIDEO_ERROR_MESSAGES = {
  RETRY: 'RETRY' as const,
  REDIRECT: 'REDIRECT' as const,
  NO_ERROR: '' as const,
};

export type ICameraStateKey = keyof typeof CAMERA_STATES_MESSAGES;
export type IMicrophoneStateKey = keyof typeof MICROPHONE_STATES_MESSAGES;
export type IInternetStateKey = keyof typeof INTERNET_STATES_MESSAGES;
export type IVideoCorruptedMessagesKey = keyof typeof VIDEO_ERROR_MESSAGES;

export const CAMERA_STATES: { [K in ICameraStateKey]: K } = {
  WAITING: 'WAITING',
  ERROR: 'ERROR',
  READY: 'READY',
  SKIP: 'SKIP',
};

export const MICROPHONE_STATES: { [K in IMicrophoneStateKey]: K } = {
  WAITING: 'WAITING',
  ERROR: 'ERROR',
  READY: 'READY',
};

export const INTERNET_STATES: { [K in IInternetStateKey]: K } = {
  DISCONNECTED: 'DISCONNECTED',
  TESTING: 'TESTING',
  SLOW_CONNECTION: 'SLOW_CONNECTION',
  CONNECTED: 'CONNECTED',
};

export const VIDEO_CORRUPTED_STATES: { [K in IVideoCorruptedMessagesKey]: K } = {
  RETRY: 'RETRY',
  REDIRECT: 'REDIRECT',
  NO_ERROR: 'NO_ERROR',
};

export interface IChecksMetaObj {
  key: 'camera' | 'microphone' | 'internet';
  state: ICameraStateKey | IMicrophoneStateKey | IInternetStateKey;
  message?: string;
}

export interface IChecksState {
  camera: ICameraStateKey,
  microphone: IMicrophoneStateKey,
  internet: IInternetStateKey
}

interface IChecksMessage {
  camera: string;
  microphone: string;
  internet: string;
}

export type VIDEO_ERRORS = Exclude<IVideoCorruptedMessagesKey, 'NO_ERROR'>;
export type MODAL_ERROR_KEY = 'MEDIA_PERMISSION' | 'NO_SOUND' | 'CONNECTION' | VIDEO_ERRORS;

export interface InitialState {
  speedTestResult: number;
  widgetConfig: IWidgetConfig;
  questions: ALL_JOB_QUESTIONS_TYPE[];
  isConnected: boolean;
  videoRef: RefObject<HTMLVideoElement>;
  networkRef: ActorRef<DoneInvokeEvent<any>> | null;
  recorderRef: ActorRef<DoneInvokeEvent<any>> | null;
  storageRef: ActorRef<DoneInvokeEvent<any>> | null;
  previewRef: ActorRef<DoneInvokeEvent<any>> | null;
  uploaderRefs: IUploaderRef;
  countDownRef: ActorRef<DoneInvokeEvent<any>> | null;
  timer: number;
  currentQuestion: number;
  currentTake: number;
  selectedTake: number | null;
  recordingType: TAKE_TYPES;
  checksState: IChecksState;
  checksMessage: IChecksMessage;
  totalFileSize: number;
  totalUploadedFilesSize: { [questionNumber: string] : number };
  totalUploadSpeed: { [questionNumber: string] : number };
  error: IVideoError | null;
  failedRecordingAttempts: number;
  failedRecordingMessage: IVideoCorruptedMessagesKey;
  isResumed: boolean;
  videoDimensions: IVideoDimensions;
  confirmUploadedFalseCount: number;
}

export interface IWidgetMachineProps {
  widgetMachine:State<InitialState, DoneInvokeEvent<any>, any, {
    value: any;
    context: InitialState;
  }, ResolveTypegenMeta<TypegenDisabled, DoneInvokeEvent<any>, BaseActionObject, ServiceMap>>;
  sendToWidget: (event: SCXML.Event<DoneInvokeEvent<any>> | SingleOrArray<Event<DoneInvokeEvent<any>>>, payload?: EventData | undefined) => void
}

export const enum STATES {
  INIT = 'init',
  WELCOME = 'welcome',
  SETUP = 'setup',
  SETUP__INIT_STORAGE = 'initStorage',
  SETUP__INIT_RECORDER = 'initRecorder',
  SETUP__TEST = 'test',
  SETUP__TEST__CAMERA = 'testCamera',
  SETUP__TEST__CAMERA__WAITING = 'testCameraWaiting',
  SETUP__TEST__CAMERA__ERROR = 'testCameraError',
  SETUP__TEST__CAMERA__SUCCESS = 'testCameraSuccess',
  SETUP__TEST__CAMERA__SKIP = 'testCameraSkip',
  SETUP__TEST__MICROPHONE = 'testMicrophone',
  SETUP__TEST__MICROPHONE__WAITING = 'testMicrophoneWaiting',
  SETUP__TEST__MICROPHONE__ERROR = 'testMicrophoneError',
  SETUP__TEST__MICROPHONE__SUCCESS = 'testMicrophoneSuccess',
  SETUP__TEST__INTERNET = 'testInternet',
  SETUP__TEST__INTERNET__IDLE = 'testInternetIdle',
  SETUP__TEST__INTERNET__TESTING = 'testInternetTesting',
  SETUP__TEST__INTERNET__FINISHED = 'testInternetFinished',
  INTERVIEW = 'interview',
  INTERVIEW__INIT = 'interviewInit',
  INTERVIEW__EXPLANATION = 'interviewExplanation',
  INTERVIEW__QUESTION = 'interviewQuestion',
  INTERVIEW__VIDEO_QUESTION = 'interviewVideoQuestion',
  INTERVIEW__ANSWER_SELECTOR = 'interviewAnswerSelector',
  INTERVIEW__ASSESSMENT = 'interviewAssessment',
  INTERVIEW__RECORDING = 'interviewRecording',
  PREVIEW = 'preview',
  TIMES_UP = 'timesUp',
  NEXT_QUESTION_MIDDLEWARE = 'nextQuestionMiddleware',
  RE_INIT_RECORDER = 'reInitRecorder',
  RE_INIT_RECORDER__NEXT_RETAKE = 'reInitRecorderNextRetake',
  RE_INIT_RECORDER__NEXT_QUESTION = 'reInitRecorderNextQuestion',
  UPLOADING = 'uploading',
  UPLOADING__SELECTOR = 'uploadingSelector',
  UPLOADING__LAST_QUESTION = 'uploadingLastQuestion',
  UPLOADING__SAFE_UPLOAD = 'uploadingSafeUpload',
  CONFIRM = 'confirm',
  CONFIRM_WATING = 'confirmWaiting',
  FINISHED = 'finished',
  ERROR = 'error',
}

export const enum ACTIONS {
  CONSOLE_LOG = 'consoleLog',
  CONSOLE_DEBUG = 'consoleDebug',
  SET_CONNECTION = 'setConnection',
  SET_CONFIG = 'getConfig',
  SET_NEXT_QUESTION = 'setNextQuestion',
  SET_SPEED_TEST = 'setSpeedTest',
  SET_CHECKS = 'setChecks',
  SET_PRACTICE_MODE = 'setPracticeMode',
  SET_QUESTION_MODE = 'setQuestionMode',
  SPAWN_NETWORK = 'spawnNetwork',
  SPAWN_RECORDER = 'spawnRecorder',
  SPAWN_UPLOADER = 'spawnUploader',
  SPAWN_STORAGE = 'spawnStorage',
  SPAWN_PREVIEW = 'spawnPreview',
  SPAWN_COUNT_DOWN = 'spawnCountDown',
  STOP_RECORDER = 'stopRecorder',
  STOP_PREVIEW = 'stopPreview',
  STOP_COUNT_DOWN = 'stopCountDown',
  STORE_TAKE = 'storeTake',
  GET_TAKE = 'getTake',
  GET_TAKES = 'getTakes',
  SEND_TAKES = 'sendTakes',
  UPDATE_VIDEO_FILE_COMPLETED = 'updateVideoFileCompleted',
  UPDATE_TOTAL_FILES_SIZE = 'updateTotalFileSize',
  UPDATE_TOTAL_UPLOADED_FILES_SIZE = 'updateTotalUploadedFileSize',
  UPDATE_QUESTION_NUMBER = 'updateQuestionNumber',
  UPDATE_RETAKES_NUMBER = 'updateRetakesNumber',
  UPDATE_TIMER = 'updateTimer',
  UPDATE_AXION_AUTH = 'updateAxiosAuth',
  RECORDER_RE_INIT = 'recorderReInit',
  DEACTIVATE_RECORDER = 'deactivateRecorder',
  RECORDER_RE_INIT_AUTO_START = 'recorderReInitAutoStart',
  UPDATE_RECORDER_PARAMS = 'updateRecorderParams',
  UPDATE_TOTAL_UPLOADED = 'updateTotalUploaded',
  SET_VIDEO_ERROR = 'setVideoError',
  UPDATE_RECORDER_SPEED_TEST = 'updateRecorderSpeedTest',
  SENTRY = 'sentry',
  SET_SESSION = 'setSession',
  SESSION_EVENT = 'sessionEvent',
  SET_EVENT_ID = 'setEventId',
  EMIT_TRACKING_EVENT = 'emitTrackingEvent',
  REVOKE_MEMORY = 'revokeMemory',
  UPDATE_VIDEO_OBJECT = 'updateVideoObject',
  UPDATE_VIDEO_PART = 'updateVideoPart',
  INCREASE_FAILED_RECORDING_ATTEMPTS = 'increaseFailedRecordingAttempts',
  RESET_FAILED_RECORDING_ATTEMPTS = 'resetFailedRecordingAttempts',
  CLEAR_VIDEO_ERROR = 'clearVideoError',
  UPDATE_VIDEO_DIMENSIONS = 'updateVideoDimensions',
  UPDATE_UPLOADED_FALSE_COUNT = 'updateUploadedFalseCount',
}

export const enum EVENTS {
  SPAWN_UPLOADER = 'SPAWN_UPLOADER',
  TEST = 'TEST',
  PRACTICE_MODE = 'PRACTICE_MODE',
  QUESTION_MODE = 'QUESTION_MODE',
  IDLE = 'IDLE',
  PREVIEW = 'PREVIEW',
  BACK = 'BACK',
  STOP_RECORDING = 'STOP_RECORDING',
  GET_TAKES = 'GET_TAKES',
  TAKE_TO_UPLOAD_RECEIVED = 'TAKE_TO_UPLOAD_RECEIVED',
  TAKES_RECEIVED = 'TAKES_RECEIVED',
  NEXT_STEP = 'NEXT_STEP',
  NEXT_QUESTION = 'NEXT_QUESTION',
  NEXT_RETAKE = 'NEXT_RETAKE',
  READY_TO_START_RECORDING = 'READY_TO_START_RECORDING',
  UPLOADER_PROGRESS = 'UPLOADER_PROGRESS',
  UPLOADER_FINISHED = 'UPLOADER_FINISHED',
  RECORDER_ERROR = 'RECORDER_ERROR',
  RETRY = 'RETRY',
  UPDATE_TIMER = 'UPDATE_TIMER',
  UPDATE_VIDEO_DIMENSIONS = 'UPDATE_VIDEO_DIMENSIONS',
}

export const enum SERVICES {
  UPDATE_VIDEO_OBJECT_CALL = 'updateVideoObjectCall',
  UPDATE_VIDEO_PART_CALL = 'updateVideoPartCall',
}

export const enum GUARDS {
  IS_VIDEO_RECORDING_SKIP = 'isVideoRecordingSkip',
  NO_STORAGE = 'noStorage',
  NO_RECORDER = 'noRecorder',
  IS_THINKING_TIME = 'isThinkingTime',
  IS_VIDEO_QUESTION = 'isVideoQuestion',
  IS_QUESTION_MODE = 'isQuestionMode',
  IS_PRACTICE_MODE = 'isPracticeMod',
  IS_NOT_LAST_TAKE = 'isNotLastTake',
  IS_LAST_INTERVIEW_QUESTION = 'isLastInterviewQuestion',
  CAN_RETEST_SPEED_CONNECTION = 'canRetestSpeedConnection',
  IS_CONNECTED = 'isConnected',
  IS_DISCONNECTED = 'isDisconnected',
  CAN_START_INTERVIEW = 'canStartInterview',
  ARE_ALL_QUESTIONS_UPLOADED = 'areAllQuestionsUploaded',
  IS_CURRENT_QUESTION_SAFE_UPLOADED = 'isCurrentQuestionSafeUploaded',
  SHOULD_SHOW_WELCOME = 'shouldShowWelcome',
  IS_NO_SOUND_ERROR = 'isNoSoundError',
  IS_VIDEO_CORRUPTED = 'isVideoCorrupted',
  IS_AUDIO_ERROR = 'isAudioError',
  IS_VIDEO_ERROR = 'isVideoError',
  THERE_ARE_NO_VIDEO_QUESTIONS_TYPE = 'thereAreNoVideoQuestionType',
  IS_RECORDER_READY = 'isRecorderReady',
  IS_ASSESSMENT_QUESTION = 'isAssessmentQuestion',
  IS_TIMES_UP = 'isTimesUp',
  SHOULD_TRY_TO_CONFIRM = 'shouldTryToConfirm',
  IS_SAFE_UPLOAD = 'isSafeUpload',
}

export const enum TAGS {
  SETUP = 'setup',
  SHOW_CAMERA = 'showCamera',
  CAN_START_RECORDING = 'canStartRecording',
  DISPLAY_OUTER_VIEW = 'displayOuterView',
  DISPLAY_QUESTION = 'displayQuestion',
  DISPLAY_QUESTIONS_LIST = 'displayQuestionsList',
  DISPLAY_UPLOAD = 'displayUpload',
  LOADING = 'loading',
}
